import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { getPraSurveyAnalysisInformation } from '@/libs/fleet-api/services/survey';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Space,
	Table,
} from 'antd';
import dayjs from 'dayjs';
import React, { Fragment, useState } from 'react';
import { useModals } from './ModalProvider';

export type PraSurveyAnalysisModalState = null | {
	prospectId: string;
};

type PraSurveyAnalysisModalProps = {
	modal: PraSurveyAnalysisModalState;
	onClose: () => void;
};

const PraSurveyAnalysisModal: React.FC<PraSurveyAnalysisModalProps> = ({
	onClose,
	modal,
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ['getPraSurveyAnalysisInformation', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPraSurveyAnalysisInformation(id);
		},
		enabled: !!modal?.prospectId,
	});

	const {
		mainInformation,
		prospectFinancialReports,
		approvalHistories,
		prospectFinancialReportAnalysis,
		surveyInformation,
	} = data?.data ?? {};

	const surveyParticipants = surveyInformation?.surveyParticipants;

	const { openPreviewModal, openMapModal, openFinancialReportModal } =
		useModals();

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}
	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<Fragment>
				<FleetTitle>Main Information</FleetTitle>
				<DataRow
					loading={isLoading}
					items={[
						[
							['Nama Customer', mainInformation?.customerName],
							['Prospect ID', mainInformation?.prospectId],
						],
						[
							['Kelas Industri', mainInformation?.industrySector.industryClass],
							['Sumber Order', mainInformation?.orderSource],
						],
						[
							[
								'Portofolio Guideline',
								mainInformation?.industrySector.industryClass,
							],
							[
								'SLIK Result',
								mainInformation?.slikResult
									? 'Kol ' + mainInformation?.slikResult
									: '-',
							],
						],
						[
							['FRM Name', mainInformation?.frm.value],
							['CA Name', mainInformation?.creditAnalyst.value],
						],
					]}
					style={{ marginBottom: 20 }}
				/>

				<Divider />

				<FleetTitle>Financial Information</FleetTitle>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						prospectFinancialReports?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<Space
										align="center"
										size="middle"
									>
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												openFinancialReportModal(record);
											}}
										>
											<FileSearchOutlined />
										</a>
									</Space>
								);
							},
						},
						{
							title: 'Laporan Keuangan',
							dataIndex: ['auditStatus', 'value'],
						},
						{
							title: 'Nama Auditor',
							dataIndex: ['auditorName'],
						},
						{
							title: 'Periode',
							dataIndex: ['periodType', 'value'],
						},
						{
							title: 'Detail Periode',
							dataIndex: ['periodTypeDetail', 'value'],
						},
						{
							title: 'Tahun',
							dataIndex: ['year'],
						},
						{
							title: 'View Dokumen',
							render: (_, record) => {
								return (
									<a
										href="#"
										className="trigger-popup"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal({
												name: record.financialReportFile.name,
												url: record.financialReportFile.url,
												id: record.financialReportFile.id as unknown as number,
											});
										}}
									>
										View Dokumen
									</a>
								);
							},
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>

				{prospectFinancialReportAnalysis &&
					prospectFinancialReportAnalysis.length > 0 && (
						<table className="table-compare">
							<thead>
								<tr>
									<td />
									{prospectFinancialReportAnalysis?.map((report, index) => (
										<td key={index}>{report?.year}</td>
									))}
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Detail Periode</td>
									{prospectFinancialReportAnalysis?.map((report, index) => (
										<td key={index}>
											<Input
												disabled
												value={report?.periodDetail}
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>EAT/Angsuran 1 TH (1X S/D 0.5X)</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.eatInstallmentRatio}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>EBIT/Angsuran 1 TH (3X S/D 1X)</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.ebitInstallmentRatio}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>Rata-Rata Saldo Mengendap/ Angsuran 1 Bulan</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.averageBalanceInstallmentRatio}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>Current Ratio*</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.currentRatio}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>Debt to Equity Ratio*</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.debtToEquityRatio}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>Average Collection Period (days)*</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.averageCollectionPeriod}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
								<tr>
									<td>Average Payable Period (days)*</td>
									{prospectFinancialReportAnalysis?.map((record, index) => (
										<td key={index}>
											<InputNumber
												value={record.averagePayablePeriod}
												readOnly
												disabled
											/>
										</td>
									))}
								</tr>
							</tbody>
						</table>
					)}

				<FleetTitle>Survey Information</FleetTitle>
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Kantor"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openMapModal({
											title: 'Lokasi Survey Kantor',
											address: surveyInformation?.officeAddress,
											latitude: surveyInformation?.officeLat,
											longitude: surveyInformation?.officeLong,
											draggable: false,
											viewOnly: true,
										});
									}}
								>
									{surveyInformation?.officeAddress}
								</a>
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Survey Kantor"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								{dayjs(surveyInformation?.surveyOfficeDate).format(
									'DD-MM-YYYY',
								)}
							</div>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Site"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openMapModal({
											title: 'Lokasi Survey Site',
											address: surveyInformation?.siteAddress,
											latitude: surveyInformation?.siteLat,
											longitude: surveyInformation?.siteLong,
											draggable: false,
											viewOnly: true,
										});
									}}
								>
									{surveyInformation?.siteAddress}
								</a>
								,
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Survey Site"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								{dayjs(surveyInformation?.surveySiteDate).format('DD-MM-YYYY')}
							</div>
						</Form.Item>
					</Col>
				</Row>

				<Divider />
				<Table
					dataSource={
						surveyParticipants?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.key + 1,
						},
						{
							title: 'Nama Peserta Survey',
							dataIndex: ['name'],
						},
						{
							title: 'Jabatan Peserta Survey',
							dataIndex: ['position'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>

				<FleetTitle>Approval History</FleetTitle>
				<Table
					dataSource={
						approvalHistories?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							width: 1,
							align: 'center',
							dataIndex: 'no',
						},
						{
							title: 'Name',
							dataIndex: 'approvalBy',
						},
						{
							title: 'Date',
							dataIndex: 'approvalDate',
						},
						{
							title: 'Process',
							dataIndex: 'approvalProcess',
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							dataIndex: 'approvalNote',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
			</Fragment>
		</FleetModal>
	);
};

export default PraSurveyAnalysisModal;

export const usePraSurveyAnalysisModal = () => {
	return useState<PraSurveyAnalysisModalState>(null);
};
